/* eslint-disable react-hooks/immutability */
import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { api, fileUrl } from "../lib/api";
import { useContentTranslation } from "../lib/useContentTranslation";
import { useDynamicManifest } from "../lib/dynamicManifest";
import { useAutoInstall } from "../lib/useAutoInstall";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import PartnerPoweredBadge from "../components/PartnerPoweredBadge";
import SEOHead from "../components/SEOHead";
import DonorInstallStrip from "../components/DonorInstallStrip";
import { Ticket, Trophy, MapPin, Calendar, ArrowRight, Tag, CalendarPlus } from "@phosphor-icons/react";
import PayPalButtons from "../components/PayPalButtons";
import StickyGiveBar from "../components/StickyGiveBar";
import GolfRegisterCTA from "../components/GolfRegisterCTA";

const API_BASE = process.env.REACT_APP_BACKEND_URL;

export default function EventPage() {
    const { slug, eventSlug } = useParams();
    const { t, i18n } = useTranslation();
    const [data, setData] = useState(null);
    const etr = useContentTranslation({
        title: data?.event?.title || "",
        description: data?.event?.description || "",
    });

    useDynamicManifest({
        manifestUrl: `${API_BASE}/api/manifest/event/${slug}/${eventSlug}`,
        appleTouchIcon: (data?.event?.image_url || data?.org?.logo_url)
            ? fileUrl(data?.event?.image_url || data?.org?.logo_url) : undefined,
        themeColor: data?.org?.primary_color || undefined,
    });
    useAutoInstall(!!data);

    // ticket purchase modal
    const [tt, setTT] = useState(null);
    const [qty, setQty] = useState(1);
    const [name, setName] = useState("");
    const [email, setEmail] = useState("");
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState("");
    const [promoCode, setPromoCode] = useState("");
    const [promoValidation, setPromoValidation] = useState(null); // {valid, discount, new_total, reason}
    const [donationAddon, setDonationAddon] = useState(0);
    const [verifyGate, setVerifyGate] = useState(null);
    const [resendingVerify, setResendingVerify] = useState(false);
    const [buyerAnswers, setBuyerAnswers] = useState({});
    const [guestAnswers, setGuestAnswers] = useState([]);
    const [guestInvites, setGuestInvites] = useState([]); // [{name,email}] for extra seats (sponsor-paid guests)
    const setGuestInvite = (idx, field, val) => setGuestInvites(prev => {
        const next = [...prev];
        while (next.length <= idx) next.push({ name: "", email: "" });
        next[idx] = { ...next[idx], [field]: val };
        return next;
    });
    const buildGuestInvites = () => {
        const extra = Math.max(0, (parseInt(qty) || 1) - 1);
        return Array.from({ length: extra }, (_, i) => guestInvites[i] || { name: "", email: "" })
            .filter(g => (g.email || "").trim());
    };

    const regQuestions = data?.event?.registration_questions || [];
    const buyerQuestions = regQuestions.filter(q => q.scope === "buyer");
    const guestQuestions = regQuestions.filter(q => q.scope === "guest");
    // Conditional logic: a question is visible only when its controlling
    // question's answer matches (mirrors backend _reg_condition_met).
    const condMet = (q, answers) => {
        const cqid = q.condition_question_id;
        if (!cqid) return true;
        const expected = (q.condition_value || "").trim().toLowerCase();
        const raw = (answers || {})[cqid];
        const actual = typeof raw === "boolean" ? (raw ? "yes" : "no") : String(raw ?? "").trim().toLowerCase();
        if (!expected) return !["", "no", "false"].includes(actual);
        if (["yes", "true", "on"].includes(expected) && ["yes", "true", "on"].includes(actual)) return true;
        return actual === expected;
    };
    const visibleBuyer = buyerQuestions.filter(q => condMet(q, buyerAnswers));
    const visibleGuest = (i) => guestQuestions.filter(q => condMet(q, guestAnswers[i] || {}));
    const setBuyerAns = (qid, val) => setBuyerAnswers(prev => ({ ...prev, [qid]: val }));
    const setGuestAns = (idx, qid, val) => setGuestAnswers(prev => {
        const next = [...prev];
        while (next.length <= idx) next.push({});
        next[idx] = { ...next[idx], [qid]: val };
        return next;
    });
    const buildGuestAnswers = () => guestQuestions.length
        ? Array.from({ length: parseInt(qty) || 1 }, (_, i) => guestAnswers[i] || {})
        : [];
    const validateRegRequired = () => {
        const empty = (v) => v === undefined || v === null || (typeof v === "string" && !v.trim());
        for (const q of buyerQuestions) {
            if (q.required && condMet(q, buyerAnswers) && empty(buyerAnswers[q.id])) return t("events.errAnswer", { label: q.label });
        }
        if (guestQuestions.length) {
            for (let i = 0; i < (parseInt(qty) || 1); i++) {
                const ans = guestAnswers[i] || {};
                for (const q of guestQuestions) {
                    if (q.required && condMet(q, ans) && empty(ans[q.id])) return t("events.errAnswerGuest", { label: q.label, n: i + 1 });
                }
            }
        }
        // Validate any guest invite emails that were filled in (they're optional, but if present must be valid)
        const extraSeats = Math.max(0, (parseInt(qty) || 1) - 1);
        for (let i = 0; i < extraSeats; i++) {
            const ge = ((guestInvites[i] || {}).email || "").trim();
            if (ge && !/^\S+@\S+\.\S+$/.test(ge)) return t("events.errGuestEmail", { n: i + 2 });
        }
        return null;
    };

    // sponsorship purchase modal
    const [sp, setSP] = useState(null);
    const [spName, setSpName] = useState("");
    const [spEmail, setSpEmail] = useState("");

    useEffect(() => { api.get(`/events/public/${slug}/${eventSlug}`).then(r => setData(r.data)); }, [slug, eventSlug]);

    const buyTickets = async (e) => {
        e.preventDefault();
        const regErr = validateRegRequired();
        if (regErr) { setErr(regErr); return; }
        setBusy(true); setErr(""); setVerifyGate(null);
        try {
            const { data: res } = await api.post(`/events/${data.event.id}/buy-tickets`, {
                ticket_type_id: tt.id, quantity: parseInt(qty),
                purchaser_name: name, purchaser_email: email,
                origin_url: window.location.origin,
                promo_code: promoCode || null,
                donation_addon: parseFloat(donationAddon) || 0,
                buyer_answers: buyerAnswers,
                guest_answers: buildGuestAnswers(),
                guest_invites: buildGuestInvites(),
                lang: (i18n.language || "en").split("-")[0],
            });
            if (res?.requires_email_verification) {
                setVerifyGate({ email: res.email, sent: res.sent, message: res.message });
                setBusy(false);
                return;
            }
            window.location.href = res.url;
        } catch (e) { setErr(e.response?.data?.detail || t("events.failed")); }
        finally { setBusy(false); }
    };

    const resendTicketVerify = async () => {
        setResendingVerify(true);
        try {
            await api.post("/email/verification/resend", { email, purpose: "ticket", name });
            setVerifyGate(g => ({ ...g, sent: true }));
        } catch (e) {
            setErr(e.response?.data?.detail || "Could not resend");
        } finally { setResendingVerify(false); }
    };

    const checkPromoCode = async () => {
        if (!promoCode.trim() || !tt) { setPromoValidation(null); return; }
        const subtotal = tt.price * parseInt(qty || 0);
        try {
            const { data: res } = await api.get(`/promo-codes/validate?code=${encodeURIComponent(promoCode)}&event_id=${data.event.id}&subtotal=${subtotal}&org_slug=${data.org.slug}`);
            setPromoValidation(res);
        } catch {
            setPromoValidation({ valid: false, reason: "Could not validate" });
        }
    };

    const buySponsorship = async (e) => {
        e.preventDefault();
        setBusy(true); setErr("");
        try {
            const { data: res } = await api.post(`/events/${data.event.id}/buy-sponsorship`, {
                sponsorship_id: sp.id, sponsor_name: spName, sponsor_email: spEmail,
                origin_url: window.location.origin,
            });
            window.location.href = res.url;
        } catch (e) { setErr(e.response?.data?.detail || t("events.failed")); }
        finally { setBusy(false); }
    };

    if (!data) return <div className="min-h-screen flex items-center justify-center"><p className="label-eyebrow">{t("common.loading")}</p></div>;
    const { org, event } = data;

    return (
        <div className="min-h-screen bg-background" data-testid="event-page" data-no-translate>
            <SEOHead
                title={`${etr.title || event.title} — ${org.name}`}
                description={(etr.description || event.description || `${event.title} hosted by ${org.name}.`).slice(0, 160)}
                canonical={`/e/${slug}/${eventSlug}`}
            />
            <Navbar />

            <section className="relative pt-32 pb-12 px-6 md:px-12 lg:px-24 bg-primary text-primary-foreground overflow-hidden">
                {event.image_url && (
                    <div className="absolute inset-0 opacity-30"><img src={fileUrl(event.image_url)} alt="" className="w-full h-full object-cover" /></div>
                )}
                <div className="relative z-10 max-w-5xl">
                    <p className="label-eyebrow text-accent">{org.name} · {t("events.event")}</p>
                    <h1 className="font-heading text-5xl md:text-7xl mt-3 leading-tight" data-testid="event-title">{etr.title || event.title}</h1>
                    <div className="flex flex-wrap items-center gap-6 mt-6 text-primary-foreground/80">
                        <span className="flex items-center gap-2"><Calendar size={16} weight="thin" /> {new Date(event.starts_at).toLocaleString()}</span>
                        {event.venue && <span className="flex items-center gap-2"><MapPin size={16} weight="thin" /> {event.venue}</span>}
                    </div>
                    <div className="flex flex-wrap gap-4 mt-6">
                        <a href={`${API_BASE}/api/events/${event.id}/calendar.ics`} download className="inline-flex items-center gap-2 text-accent label-eyebrow hover:underline" data-testid="add-to-calendar">
                            <CalendarPlus size={14} /> {t("events.addToCalendar")}
                        </a>
                        <a
                            href={`https://calendar.google.com/calendar/render?action=TEMPLATE&text=${encodeURIComponent(event.title)}&dates=${new Date(event.starts_at).toISOString().replace(/[-:]|\.\d{3}/g, "")}/${new Date(event.ends_at || event.starts_at).toISOString().replace(/[-:]|\.\d{3}/g, "")}&details=${encodeURIComponent(event.description || "")}&location=${encodeURIComponent(event.venue || "")}`}
                            target="_blank" rel="noopener noreferrer"
                            className="inline-flex items-center gap-2 text-accent label-eyebrow hover:underline"
                            data-testid="add-to-google-calendar"
                        >
                            <CalendarPlus size={14} /> {t("events.addToGoogle")}
                        </a>
                    </div>
                </div>
            </section>

            <GolfRegisterCTA eventId={event.id} />

            <div className="px-6 md:px-12 lg:px-24 py-16 max-w-6xl mx-auto space-y-16">
                {event.description && <p className="text-lg leading-relaxed max-w-3xl">{etr.description || event.description}</p>}

                {/* TICKETS */}
                {event.ticket_types?.length > 0 && (
                    <div id="tickets">
                        <p className="label-eyebrow text-accent">{t("events.tickets")}</p>
                        <h2 className="font-heading text-4xl mt-2 mb-6">{t("events.getTickets")}</h2>
                        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="ticket-types-grid">
                            {event.ticket_types.map(t2 => {
                                const remaining = t2.quantity_total - t2.quantity_sold;
                                return (
                                    <div key={t2.id} className="ed-card p-6 space-y-3" data-testid={`ticket-type-${t2.id}`}>
                                        <Ticket size={28} weight="thin" className="text-accent" />
                                        <h3 className="font-heading text-2xl">{t2.name}</h3>
                                        <p className="data-num text-3xl">${t2.price.toFixed(2)}</p>
                                        {t2.description && <p className="text-muted-foreground text-sm">{t2.description}</p>}
                                        <p className="text-xs label-eyebrow">{t("events.remaining", { n: remaining, total: t2.quantity_total })}</p>
                                        <button onClick={() => { setTT(t2); setQty(t2.min_purchase || 1); setBuyerAnswers({}); setGuestAnswers([]); setErr(""); }}
                                                disabled={remaining <= 0}
                                                className="btn-accent w-full" data-testid={`buy-ticket-${t2.id}`}>
                                            {remaining <= 0 ? t("events.soldOut") : t("events.buyNow")}
                                        </button>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                )}

                {/* SPONSORSHIPS */}
                {event.sponsorship_packages?.length > 0 && (
                    <div id="sponsorships">
                        <p className="label-eyebrow text-accent">{t("events.sponsorEyebrow")}</p>
                        <h2 className="font-heading text-4xl mt-2 mb-6">{t("events.sponsorPackages")}</h2>
                        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="sponsorship-grid">
                            {event.sponsorship_packages.map(s => {
                                const soldOut = s.quantity_total && s.quantity_sold >= s.quantity_total;
                                return (
                                    <div key={s.id} className="ed-card p-6 space-y-3 border-2 border-accent" data-testid={`sponsorship-pkg-${s.id}`}>
                                        <Trophy size={28} weight="thin" className="text-accent" />
                                        <h3 className="font-heading text-2xl">{s.name}</h3>
                                        <p className="data-num text-3xl">${s.price.toFixed(0)}</p>
                                        {s.description && <p className="text-muted-foreground text-sm">{s.description}</p>}
                                        {s.benefits?.length > 0 && (
                                            <ul className="text-sm space-y-1 pt-2 border-t border-border">
                                                {s.benefits.map((b, i) => <li key={i}>✓ {b}</li>)}
                                            </ul>
                                        )}
                                        <button onClick={() => { setSP(s); setSpName(""); setSpEmail(""); setErr(""); }}
                                                disabled={soldOut} className="btn-primary w-full" data-testid={`buy-sponsorship-${s.id}`}>
                                            {soldOut ? t("events.soldOut") : t("events.becomeSponsor")}
                                        </button>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                )}
            </div>

            {/* TICKET MODAL */}
            {tt && (
                <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-6" onClick={() => setTT(null)}>
                    <form onSubmit={buyTickets} onClick={e => e.stopPropagation()} className="bg-card border border-border max-w-md w-full p-8 space-y-5 max-h-[90vh] overflow-y-auto" data-testid="ticket-purchase-modal">
                        <h2 className="font-heading text-3xl">{t("events.buyTitle", { name: tt.name })}</h2>
                        <p className="data-num text-2xl text-accent">${tt.price.toFixed(2)} {t("events.each")}</p>
                        <div>
                            <label className="label-eyebrow block mb-1">{t("events.quantity")}</label>
                            <input type="number" min={tt.min_purchase || 1} max={Math.min(tt.quantity_total - tt.quantity_sold, tt.max_purchase || (tt.quantity_total - tt.quantity_sold))} className="ed-input data-num text-xl" value={qty} onChange={e => setQty(e.target.value)} data-testid="ticket-qty" />
                            {(tt.min_purchase > 1 || tt.max_purchase) && (
                                <p className="text-xs text-muted-foreground mt-1" data-testid="ticket-qty-limits">
                                    {tt.min_purchase > 1 ? t("events.minQty", { n: tt.min_purchase }) : ""}{tt.min_purchase > 1 && tt.max_purchase ? " · " : ""}{tt.max_purchase ? t("events.maxQty", { n: tt.max_purchase }) : ""} {t("events.perOrder")}
                                </p>
                            )}
                        </div>
                        <div>
                            <label className="label-eyebrow block mb-1">{t("events.name")}</label>
                            <input className="ed-input" required value={name} onChange={e => setName(e.target.value)} data-testid="ticket-name" />
                        </div>
                        <div>
                            <label className="label-eyebrow block mb-1">{t("events.email")}</label>
                            <input className="ed-input" type="email" required value={email} onChange={e => setEmail(e.target.value)} data-testid="ticket-email" />
                        </div>

                        {/* Buyer-scope registration questions */}
                        {visibleBuyer.length > 0 && (
                            <div className="space-y-3" data-testid="reg-buyer-questions">
                                {visibleBuyer.map(q => (
                                    <RegField key={q.id} q={q} value={buyerAnswers[q.id]} onChange={v => setBuyerAns(q.id, v)} testid={`reg-buyer-${q.id}`} />
                                ))}
                            </div>
                        )}

                        {/* Per-guest registration questions */}
                        {guestQuestions.length > 0 && Array.from({ length: parseInt(qty) || 1 }, (_, i) => (
                            <div key={i} className="border-t border-border pt-4 space-y-3" data-testid={`reg-guest-block-${i}`}>
                                <p className="label-eyebrow text-accent">{t("events.guest", { n: i + 1 })}</p>
                                {visibleGuest(i).map(q => (
                                    <RegField key={q.id} q={q} value={(guestAnswers[i] || {})[q.id]} onChange={v => setGuestAns(i, q.id, v)} testid={`reg-guest-${i}-${q.id}`} />
                                ))}
                            </div>
                        ))}

                        {/* Who's coming? — collect each extra seat's contact so we can
                            invite them to claim a bidder number (card on file). */}
                        {(parseInt(qty) || 1) > 1 && (
                            <div className="border-t border-border pt-4 space-y-3" data-testid="guest-invites-section">
                                <div>
                                    <p className="label-eyebrow text-accent flex items-center gap-1"><Ticket size={12} /> {t("events.whoComing")}</p>
                                    <p className="text-xs text-muted-foreground mt-1">
                                        {t("events.whoComingDesc")}
                                    </p>
                                </div>
                                {Array.from({ length: (parseInt(qty) || 1) - 1 }, (_, i) => (
                                    <div key={i} className="space-y-2" data-testid={`guest-invite-${i}`}>
                                        <div className="grid grid-cols-2 gap-2">
                                            <input className="ed-input" placeholder={t("events.guestName", { n: i + 2 })}
                                                value={(guestInvites[i] || {}).name || ""}
                                                onChange={e => setGuestInvite(i, "name", e.target.value)}
                                                data-testid={`guest-invite-name-${i}`} />
                                            <input className={`ed-input ${((guestInvites[i] || {}).email || "").trim() && !/^\S+@\S+\.\S+$/.test(((guestInvites[i] || {}).email || "").trim()) ? "border-destructive" : ""}`} type="email" placeholder={t("events.guestEmailPlaceholder")}
                                                value={(guestInvites[i] || {}).email || ""}
                                                onChange={e => setGuestInvite(i, "email", e.target.value)}
                                                data-testid={`guest-invite-email-${i}`} />
                                        </div>
                                        <input className="ed-input w-full" type="tel" placeholder={t("events.guestPhone")}
                                            value={(guestInvites[i] || {}).phone || ""}
                                            onChange={e => setGuestInvite(i, "phone", e.target.value)}
                                            data-testid={`guest-invite-phone-${i}`} />
                                    </div>
                                ))}
                            </div>
                        )}

                        {/* Promo code */}
                        <div data-testid="promo-section">
                            <label className="label-eyebrow block mb-1 flex items-center gap-1"><Tag size={12} /> {t("events.promoCode")}</label>
                            <div className="flex gap-2">
                                <input className="ed-input flex-1 data-num uppercase" value={promoCode} onChange={e => { setPromoCode(e.target.value); setPromoValidation(null); }} placeholder="SAVE10" data-testid="promo-input" />
                                <button type="button" onClick={checkPromoCode} disabled={!promoCode.trim()} className="btn-ghost text-sm" data-testid="apply-promo">{t("events.apply")}</button>
                            </div>
                            {promoValidation && (
                                promoValidation.valid
                                    ? <p className="text-xs text-accent mt-2" data-testid="promo-applied">{t("events.promoApplied", { amount: promoValidation.discount.toFixed(2) })}</p>
                                    : <p className="text-xs text-destructive mt-2" data-testid="promo-invalid">{promoValidation.reason}</p>
                            )}
                        </div>

                        {/* Donation add-on */}
                        <div className="border-t border-border pt-4" data-testid="donation-upsell">
                            <label className="flex items-start gap-3 text-sm cursor-pointer">
                                <input type="checkbox" className="mt-1" checked={donationAddon > 0} onChange={e => setDonationAddon(e.target.checked ? 25 : 0)} data-testid="addon-toggle" />
                                <span className="flex-1">
                                    <span className="font-semibold">{t("events.addDonation")}</span>
                                    <span className="block text-xs text-muted-foreground mt-1">{t("events.addDonationDesc")}</span>
                                </span>
                            </label>
                            {donationAddon > 0 && (
                                <div className="mt-3 flex gap-2">
                                    {[10, 25, 50, 100].map(v => (
                                        <button type="button" key={v} onClick={() => setDonationAddon(v)} className={`flex-1 py-2 border text-sm data-num ${parseFloat(donationAddon) === v ? "border-accent bg-accent text-accent-foreground" : "border-border"}`}>${v}</button>
                                    ))}
                                </div>
                            )}
                        </div>

                        <div className="pt-3 border-t border-border space-y-1 text-sm">
                            <div className="flex justify-between"><span className="text-muted-foreground">{t("events.ticketsLine", { qty: qty, price: tt.price.toFixed(2) })}</span><span className="data-num">${(tt.price * qty).toFixed(2)}</span></div>
                            {promoValidation?.valid && <div className="flex justify-between text-accent"><span>{t("events.promoDiscount")}</span><span className="data-num">−${promoValidation.discount.toFixed(2)}</span></div>}
                            {donationAddon > 0 && <div className="flex justify-between"><span className="text-muted-foreground">{t("events.donation")}</span><span className="data-num">${parseFloat(donationAddon).toFixed(2)}</span></div>}
                            <div className="flex justify-between pt-2 border-t border-border">
                                <span className="label-eyebrow">{t("events.total")}</span>
                                <span className="data-num text-xl text-accent" data-testid="ticket-total">
                                    ${(Math.max(0, tt.price * qty - (promoValidation?.valid ? promoValidation.discount : 0)) + parseFloat(donationAddon || 0)).toFixed(2)}
                                </span>
                            </div>
                        </div>

                        {err && <p className="text-destructive text-sm">{err}</p>}
                        {verifyGate && (
                            <div className="bg-amber-50 border-2 border-amber-500 p-4 space-y-3" data-testid="ticket-verify-gate">
                                <p className="font-bold text-amber-900 text-base flex items-center gap-2">
                                    <span className="text-xl">✉</span> {t("events.verifyTitle")}
                                </p>
                                <p className="text-sm text-amber-900">
                                    {t("events.verifyBody", { email: verifyGate.email })}
                                </p>
                                <p className="text-xs text-amber-800">{t("events.verifyWhy")}</p>
                                <div className="flex gap-2">
                                    <button type="button" onClick={resendTicketVerify} disabled={resendingVerify}
                                            className="border border-amber-700 text-amber-900 px-4 py-2 text-xs uppercase tracking-wider font-bold disabled:opacity-50"
                                            data-testid="ticket-verify-resend">
                                        {resendingVerify ? t("events.sending") : t("events.resendLink")}
                                    </button>
                                    <button type="button" onClick={() => setVerifyGate(null)}
                                            className="text-amber-800 px-3 py-2 text-xs uppercase tracking-wider underline"
                                            data-testid="ticket-verify-dismiss">
                                        {t("events.clickedTryAgain")}
                                    </button>
                                </div>
                            </div>
                        )}
                        <button type="submit" disabled={busy} className="btn-accent w-full" data-testid="confirm-ticket-purchase">
                            {busy ? t("common.loading") : <>{t("events.payCard")} <ArrowRight size={16} weight="bold" /></>}
                        </button>
                        <div className="flex items-center gap-2 my-1">
                            <div className="flex-1 h-px bg-border" />
                            <span className="text-xs label-eyebrow text-muted-foreground">{t("events.or")}</span>
                            <div className="flex-1 h-px bg-border" />
                        </div>
                        <PayPalButtons
                            captureType="ticket"
                            validate={() => {
                                if (!name.trim()) return t("events.errName");
                                if (!email.trim()) return t("events.errEmail");
                                if (!(parseInt(qty) > 0)) return t("events.errQty");
                                const regErr = validateRegRequired();
                                if (regErr) return regErr;
                                return null;
                            }}
                            createOrder={async () => {
                                const { data: res } = await api.post("/paypal/tickets/create-order", {
                                    event_id: data.event.id,
                                    ticket_type_id: tt.id,
                                    quantity: parseInt(qty),
                                    purchaser_name: name,
                                    purchaser_email: email,
                                    buyer_answers: buyerAnswers,
                                    guest_answers: buildGuestAnswers(),
                                    lang: (i18n.language || "en").split("-")[0],
                                });
                                return res.order_id;
                            }}
                            onSuccess={() => { window.location.href = `/payment/success?session_id=paypal_success&provider=paypal`; }}
                            onError={(e) => setErr(e?.response?.data?.detail || e?.message || "PayPal error")}
                        />
                    </form>
                </div>
            )}

            {/* SPONSORSHIP MODAL */}
            {sp && (
                <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-6" onClick={() => setSP(null)}>
                    <form onSubmit={buySponsorship} onClick={e => e.stopPropagation()} className="bg-card border border-border max-w-md w-full p-8 space-y-5" data-testid="sponsorship-modal">
                        <h2 className="font-heading text-3xl">{sp.name}</h2>
                        <p className="data-num text-2xl text-accent">${sp.price.toFixed(0)}</p>
                        <div>
                            <label className="label-eyebrow block mb-1">{t("events.sponsorName")}</label>
                            <input className="ed-input" required value={spName} onChange={e => setSpName(e.target.value)} data-testid="sponsor-name" />
                        </div>
                        <div>
                            <label className="label-eyebrow block mb-1">{t("events.email")}</label>
                            <input className="ed-input" type="email" required value={spEmail} onChange={e => setSpEmail(e.target.value)} data-testid="sponsor-email" />
                        </div>
                        {err && <p className="text-destructive text-sm">{err}</p>}
                        <button type="submit" disabled={busy} className="btn-accent w-full" data-testid="confirm-sponsorship">
                            {busy ? t("common.loading") : <>{t("events.continuePayment")} <ArrowRight size={16} weight="bold" /></>}
                        </button>
                    </form>
                </div>
            )}

            {(event.ticket_types?.length > 0 || event.sponsorship_packages?.length > 0) && (
                <StickyGiveBar
                    targetId={event.ticket_types?.length > 0 ? "tickets" : "sponsorships"}
                    ctaLabel={event.ticket_types?.length > 0 ? t("events.getTickets") : t("events.becomeSponsor")}
                    contextLabel={event.ticket_types?.length > 0 ? t("events.tickets") : t("events.sponsorEyebrow")}
                    contextValue={event.ticket_types?.length > 0
                        ? t("events.fromPrice", { price: Math.min(...event.ticket_types.map(x => x.price)).toFixed(0) })
                        : null}
                />
            )}

            <DonorInstallStrip orgName={event.title || org.name} />
            <PartnerPoweredBadge partner={data.partner} className="bg-secondary/10 border-t border-border" />
            <Footer />
        </div>
    );
}


function RegField({ q, value, onChange, testid }) {
    const { t } = useTranslation();
    const label = (
        <label className="label-eyebrow block mb-1">
            {q.label}{q.required && <span className="text-destructive"> *</span>}
        </label>
    );
    if (q.type === "select") {
        return (
            <div>
                {label}
                <select className="ed-input w-full" value={value || ""} onChange={e => onChange(e.target.value)} data-testid={testid}>
                    <option value="">{t("events.select")}</option>
                    {(q.options || []).map(o => <option key={o} value={o}>{o}</option>)}
                </select>
            </div>
        );
    }
    if (q.type === "textarea") {
        return (
            <div>
                {label}
                <textarea className="ed-input w-full" rows={2} value={value || ""} onChange={e => onChange(e.target.value)} data-testid={testid} />
            </div>
        );
    }
    if (q.type === "checkbox") {
        return (
            <label className="flex items-center gap-2 text-sm cursor-pointer">
                <input type="checkbox" checked={!!value} onChange={e => onChange(e.target.checked)} data-testid={testid} />
                <span>{q.label}{q.required && <span className="text-destructive"> *</span>}</span>
            </label>
        );
    }
    return (
        <div>
            {label}
            <input className="ed-input w-full" value={value || ""} onChange={e => onChange(e.target.value)} data-testid={testid} />
        </div>
    );
}
